import React, { useEffect, useState } from "react";
import { apiFetch } from "../utils/api";
import { toast } from "../components/Toast";

const Trips = () => {
  const [trips, setTrips] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // form stores route_code (for display) + route_id (resolved from bus code selection)
  const [form, setForm] = useState({
    trip_id: "",
    route_code: "",
    route_name: "",
    route_id: "",
    driver_id: "",
    bus_number: "",
  });

  const [vehicles, setVehicles] = useState([]);
  const [uniqueRoutes, setUniqueRoutes] = useState([]); // deduplicated by route_code
  const [drivers, setDrivers] = useState([]);
  const [busCodes, setBusCodes] = useState([]); // { route_id, vehicle_code } for selected route_code
  const [submitting, setSubmitting] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [modalError, setModalError] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");

  const ITEMS_PER_PAGE = 10;

  // Filter trips by ID, bus code, route, or driver
  const filteredTrips = trips.filter((trip) => {
    if (!searchQuery.trim()) return true;
    const query = searchQuery.toLowerCase();
    const tripId = (trip.trip_id || "").toLowerCase();
    const busCode = (trip.vehicle_code || trip.bus_number || "").toLowerCase();
    const route = (trip.route_name || "").toLowerCase();
    const driver = (trip.driver_name || "").toLowerCase();
    return (
      tripId.includes(query) ||
      busCode.includes(query) ||
      route.includes(query) ||
      driver.includes(query)
    );
  });

  // Show all trips including completed
  const visibleTrips = filteredTrips;
  const totalPages = Math.ceil(visibleTrips.length / ITEMS_PER_PAGE);
  const paginatedTrips = visibleTrips.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE,
  );

  const handlePrevPage = () => setCurrentPage((p) => Math.max(1, p - 1));
  const handleNextPage = () =>
    setCurrentPage((p) => Math.min(totalPages, p + 1));

  const emptyForm = () => ({
    trip_id: "",
    route_code: "",
    route_name: "",
    route_id: "",
    driver_id: "",
    bus_number: "",
  });

  const loadData = async () => {
    setLoading(true);

    try {
      const [tRes, vRes, rRes, dRes] = await Promise.all([
        apiFetch("/trips").catch(() => ({ data: { data: [] } })),
        apiFetch("/vehicles/active").catch(() => ({ data: { data: [] } })),
        apiFetch("/routes/by-code").catch(() => ({ data: { data: [] } })),
        apiFetch("/drivers").catch(() => ({ data: { data: [] } })),
      ]);

      console.log("ROUTES API RESPONSE:", rRes);
      console.log("ROUTES API RESPONSE.data:", rRes?.data);
      console.log(
        "ROUTES API RESPONSE.data is array:",
        Array.isArray(rRes?.data),
      );
      console.log("ROUTES API RESPONSE.status:", rRes?.status);

      // Backend returns { status: "success", data: [...] } where data is the array directly
      const routesArray = Array.isArray(rRes?.data) ? rRes.data : [];

      console.log("FINAL routesArray to set:", routesArray);
      console.log("FINAL routesArray length:", routesArray.length);

      console.log("Parsed routes array:", routesArray);
      console.log("Parsed routes array length:", routesArray.length);

      setTrips(Array.isArray(tRes?.data) ? tRes.data : []);
      setVehicles(Array.isArray(vRes?.data) ? vRes.data : []);
      setUniqueRoutes(routesArray);
      setDrivers(Array.isArray(dRes?.data) ? dRes.data : []);
    } catch (e) {
      console.error(e);
      toast.error("Failed to fetch trips data.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  // Debug uniqueRoutes state changes
  useEffect(() => {
    console.log("[ROUTES DEBUG] uniqueRoutes state updated:", uniqueRoutes);
    console.log(
      "[ROUTES DEBUG] Is uniqueRoutes an array?",
      Array.isArray(uniqueRoutes),
    );
    console.log("[ROUTES DEBUG] uniqueRoutes.length:", uniqueRoutes.length);
    console.log("[ROUTES DEBUG] First item in uniqueRoutes:", uniqueRoutes[0]);
  }, [uniqueRoutes]);

  // When route_code changes, fetch only AVAILABLE bus codes (not scheduled or active)
  const handleRouteCodeChange = (e) => {
    const code = e.target.value;
    setForm({ ...form, route_code: code, route_id: "", bus_number: "" });
    setBusCodes([]);
    if (code) {
      apiFetch(
        `/routes/available-bus-codes?route_code=${encodeURIComponent(code)}`,
      )
        .then((res) => setBusCodes(res.data || []))
        .catch(() => setBusCodes([]));
    }
  };

  // When bus code is selected, resolve the route_id
  const handleBusCodeChange = (e) => {
    const vehicleCode = e.target.value;
    const entry = busCodes.find((b) => b.vehicle_code === vehicleCode);
    setForm({
      ...form,
      bus_number: vehicleCode,
      route_id: entry?.route_id || "",
    });
  };

  // When editing, pre-load all bus codes for the trip's route_code (for editing existing trip)
  const loadBusCodesForRouteCode = async (routeCode, currentBusNumber) => {
    if (!routeCode) {
      setBusCodes([]);
      return;
    }
    try {
      const res = await apiFetch(
        `/routes/bus-codes?route_code=${encodeURIComponent(routeCode)}`,
      );
      setBusCodes(res.data?.data || []);
    } catch {
      setBusCodes([]);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.bus_number) {
      toast.error("Please select a bus code");
      return;
    }
    if (!form.route_id) {
      toast.error("Route could not be resolved from bus code");
      return;
    }

    // Check if driver is already assigned to a SCHEDULED or ACTIVE trip (exclude current trip when editing)
    if (form.driver_id) {
      const driverId = String(form.driver_id);
      const existingTrip = trips.find(
        (t) =>
          String(t.driver_id) === driverId &&
          t.trip_id !== form.trip_id &&
          (t.status === "scheduled" || t.status === "active"),
      );
      if (existingTrip) {
        toast.error(
          `Driver already has a ${existingTrip.status} trip (Bus: ${existingTrip.vehicle_code || existingTrip.bus_number || "Unknown"}). Stop or delete that trip first.`,
        );
        return;
      }
    }

    setSubmitting(true);
    try {
      const payload = {
        route_id: form.route_id,
        driver_id: form.driver_id,
        bus_number: form.bus_number,
      };
      if (form.trip_id) {
        const res = await apiFetch(`/trips/${form.trip_id}`, {
          method: "PUT",
          body: JSON.stringify(payload),
        });
        if (res.status === 409 || res.code === "DRIVER_BUSY") {
          setModalError(
            res.message || "Driver is already assigned to another trip",
          );
          setSubmitting(false);
          return;
        }
        toast.success("Trip updated");
      } else {
        const res = await apiFetch("/trips", {
          method: "POST",
          body: JSON.stringify(payload),
        });
        if (res.status === 409 || res.code === "DRIVER_BUSY") {
          setModalError(
            res.message || "Driver is already assigned to another trip",
          );
          setSubmitting(false);
          return;
        }
        toast.success("Trip scheduled");
      }
      setIsModalOpen(false);
      setForm(emptyForm());
      setBusCodes([]);
      loadData();
    } catch (e) {
      const code = e.code || "";
      const msg = e.message || "";
      if (code === "BUS_ALREADY_SCHEDULED") toast.error(msg);
      else if (code === "BUS_ACTIVE" || msg.includes("already running"))
        toast.error("Bus already running an active trip");
      else if (
        code === "VEHICLE_ACTIVE" ||
        (msg.includes("vehicle") && msg.includes("active"))
      )
        toast.error("Vehicle already in use on another trip");
      else if (code === "DRIVER_CONFLICT" || msg.includes("Driver already"))
        toast.error("Driver already assigned to another trip");
      else toast.error(msg || "Failed to schedule trip");
    } finally {
      setSubmitting(false);
    }
  };

  const handleEditTrip = async (trip) => {
    // Find the route_code for this trip's route_id
    const allRoutes = await apiFetch("/routes").catch(() => ({ data: [] }));
    const routeRow = (allRoutes.data?.data || []).find(
      (r) => r.route_id === trip.route_id,
    );
    const routeCode = routeRow?.route_code || "";

    setForm({
      trip_id: trip.trip_id,
      route_code: routeCode,
      route_name: trip.route_name || "",
      route_id: trip.route_id,
      driver_id: trip.driver_id,
      bus_number: trip.bus_number || trip.vehicle_code || "",
    });
    await loadBusCodesForRouteCode(
      routeCode,
      trip.bus_number || trip.vehicle_code,
    );
    setIsModalOpen(true);
  };

  const handleDeleteTrip = async (tripId) => {
    if (!window.confirm("Delete this trip?")) return;
    try {
      await apiFetch(`/trips/${tripId}`, { method: "DELETE" });
      toast.success("Deleted");
      loadData();
    } catch (e) {
      toast.error(e.message || "Failed to delete");
    }
  };

  const handleStartTrip = async (trip) => {
    try {
      await apiFetch(`/trips/${trip.trip_id}/start`, {
        method: "PATCH",
        body: JSON.stringify({ driver_id: trip.driver_id }),
      });
      toast.success("Trip activated!");
      loadData();
    } catch (e) {
      toast.error(e.message);
    }
  };

  const handleStopTrip = async (tripId) => {
    if (
      !window.confirm(
        "Stop this trip? It will be discontinued and returned to scheduled status.",
      )
    )
      return;
    try {
      await apiFetch(`/trips/${tripId}/stop`, { method: "POST" });
      toast.success("Trip stopped and returned to scheduled!");
      loadData();
    } catch (e) {
      toast.error(e.message);
    }
  };

  const handleEndTrip = async (tripId) => {
    if (
      !window.confirm(
        "End this trip? It will transition to returning status for the return journey.",
      )
    )
      return;
    try {
      await apiFetch(`/trips/${tripId}/end`, { method: "POST" });
      toast.success("Trip ended. Return journey can now begin.");
      loadData();
    } catch (e) {
      toast.error(e.message || "Failed to end trip");
    }
  };

  const handleParkTrip = async (tripId) => {
    if (
      !window.confirm(
        "Park vehicle? This will complete the trip and return it to scheduled status.",
      )
    )
      return;
    try {
      await apiFetch(`/trips/${tripId}/park`, { method: "POST" });
      toast.success("Vehicle parked. Trip completed successfully!");
      loadData();
    } catch (e) {
      toast.error(e.message || "Failed to park vehicle");
    }
  };

  const handleCloneTrip = async (tripId) => {
    try {
      const res = await apiFetch(`/trips/${tripId}/clone`, { method: "POST" });
      if (
        res.status === 409 ||
        res.code === "DRIVER_BUSY" ||
        res.code === "BUS_ALREADY_SCHEDULED"
      ) {
        toast.error(
          res.message ||
            "Cannot clone trip. Driver or bus is already assigned to another trip.",
        );
        return;
      }
      toast.success("Trip cloned successfully! New scheduled trip created.");
      loadData();
    } catch (e) {
      toast.error(e.message || "Failed to clone trip");
    }
  };

  const fmtId = (id) =>
    id ? `TRIP-${String(id).slice(0, 4).toUpperCase()}` : "TRIP----";

  return (
    <div>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "24px",
        }}
      >
        <h1 className="page-title text-gradient" style={{ margin: 0 }}>
          Trip Dispatcher
        </h1>
        <div style={{ display: "flex", gap: "12px", alignItems: "center" }}>
          <input
            type="text"
            placeholder="Search by trip ID, bus, route or driver..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="form-control"
            style={{ width: "300px", height: "40px" }}
          />
          <button
            className="btn-primary"
            onClick={() => {
              setForm(emptyForm());
              setBusCodes([]);
              setIsModalOpen(true);
            }}
            style={{ height: "40px" }}
          >
            + Schedule Trip
          </button>
        </div>
      </div>

      <div
        className="data-table-container glass-panel"
        style={{ display: "flex", flexDirection: "column" }}
      >
        <table className="data-table" style={{ flex: 1, fontSize: "14px" }}>
          <thead>
            <tr style={{ height: "48px" }}>
              <th style={{ padding: "10px 16px" }}>Trip ID</th>
              <th style={{ padding: "10px 16px" }}>Bus Code</th>
              <th style={{ padding: "10px 16px" }}>Route</th>
              <th style={{ padding: "10px 16px" }}>Driver</th>
              <th style={{ padding: "10px 16px" }}>Status</th>
              <th style={{ padding: "10px 16px" }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr style={{ height: "48px" }}>
                <td
                  colSpan="6"
                  style={{ textAlign: "center", padding: "10px 16px" }}
                >
                  Loading...
                </td>
              </tr>
            ) : visibleTrips.length === 0 ? (
              <tr style={{ height: "48px" }}>
                <td
                  colSpan="6"
                  style={{ textAlign: "center", padding: "10px 16px" }}
                >
                  No trips scheduled.
                </td>
              </tr>
            ) : (
              paginatedTrips.map((trip) => (
                <tr key={trip.trip_id} style={{ height: "48px" }}>
                  <td
                    style={{
                      fontSize: "0.8rem",
                      fontWeight: 700,
                      letterSpacing: "0.04em",
                      padding: "10px 16px",
                    }}
                  >
                    {fmtId(trip.trip_id)}
                  </td>
                  <td style={{ padding: "10px 16px" }}>
                    <span className="badge badge-active">
                      {trip.vehicle_code || trip.bus_number || "—"}
                    </span>
                  </td>
                  <td style={{ padding: "10px 16px" }}>
                    {trip.route_name || "—"}
                  </td>
                  <td style={{ padding: "10px 16px" }}>
                    {trip.driver_name || "—"}
                  </td>
                  <td style={{ padding: "10px 16px" }}>
                    {trip.status === "active" ? (
                      <span
                        style={{
                          padding: "3px 10px",
                          background: "#e6f4ea",
                          color: "#1e8e3e",
                          borderRadius: "12px",
                          fontSize: "12px",
                          fontWeight: "600",
                          textTransform: "uppercase",
                        }}
                      >
                        Active
                      </span>
                    ) : trip.status === "returning" ? (
                      <span
                        style={{
                          padding: "3px 10px",
                          background: "#fff3cd",
                          color: "#856404",
                          borderRadius: "12px",
                          fontSize: "12px",
                          fontWeight: "600",
                          textTransform: "uppercase",
                        }}
                      >
                        Returning
                      </span>
                    ) : (
                      <span
                        style={{
                          padding: "3px 10px",
                          background: "#f0f0f0",
                          color: "#000",
                          borderRadius: "12px",
                          fontSize: "12px",
                          fontWeight: "600",
                          textTransform: "uppercase",
                        }}
                      >
                        Scheduled
                      </span>
                    )}
                  </td>
                  <td style={{ padding: "10px 16px" }}>
                    <div className="table-actions">
                      {trip.status === "scheduled" && (
                        <>
                          <button
                            className="btn-secondary"
                            onClick={() => handleEditTrip(trip)}
                          >
                            Edit
                          </button>
                          <button
                            className="btn-primary"
                            onClick={() => handleStartTrip(trip)}
                          >
                            Start
                          </button>
                          <button
                            className="btn-danger"
                            onClick={() => handleDeleteTrip(trip.trip_id)}
                          >
                            Delete
                          </button>
                        </>
                      )}
                      {trip.status === "active" && (
                        <>
                          <button
                            className="btn-primary"
                            onClick={() => handleEndTrip(trip.trip_id)}
                          >
                            End Trip
                          </button>
                          <button
                            className="btn-secondary"
                            onClick={() => handleStopTrip(trip.trip_id)}
                          >
                            Stop
                          </button>
                        </>
                      )}
                      {trip.status === "returning" && (
                        <>
                          <button
                            className="btn-primary"
                            onClick={() => handleParkTrip(trip.trip_id)}
                          >
                            Park
                          </button>
                          <button
                            className="btn-danger"
                            onClick={() => handleStopTrip(trip.trip_id)}
                          >
                            Stop
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>

        {/* Pagination */}
        {visibleTrips.length > 0 && (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              gap: "16px",
              padding: "16px",
              borderTop: "1px solid var(--border-color)",
            }}
          >
            <button
              className="btn-secondary"
              onClick={handlePrevPage}
              disabled={currentPage === 1}
              style={{ opacity: currentPage === 1 ? 0.5 : 1 }}
            >
              ← Previous
            </button>
            <span style={{ color: "var(--text-muted)" }}>
              Page {currentPage} of {totalPages}
            </span>
            <button
              className="btn-secondary"
              onClick={handleNextPage}
              disabled={currentPage === totalPages}
              style={{ opacity: currentPage === totalPages ? 0.5 : 1 }}
            >
              Next →
            </button>
          </div>
        )}
      </div>

      {isModalOpen && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h2 className="modal-title">
                {form.trip_id ? "Edit Trip" : "Schedule Trip"}
              </h2>
              <button
                className="modal-close"
                type="button"
                onClick={() => setIsModalOpen(false)}
              >
                &times;
              </button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label className="form-label">Route</label>
                {form.trip_id ? (
                  // Editing: Show as read-only text
                  <div
                    className="form-control"
                    style={{
                      backgroundColor: "#f5f5f5",
                      cursor: "not-allowed",
                    }}
                  >
                    {form.route_name || "—"}
                  </div>
                ) : (
                  // Creating: Show as dropdown
                  <>
                    {(() => {
                      console.log(
                        "[RENDER DEBUG] uniqueRoutes before render:",
                        uniqueRoutes,
                      );
                      console.log(
                        "[RENDER DEBUG] Is uniqueRoutes array?",
                        Array.isArray(uniqueRoutes),
                      );
                      console.log(
                        "[RENDER DEBUG] uniqueRoutes.length:",
                        uniqueRoutes.length,
                      );
                      console.log(
                        "[RENDER DEBUG] First route in render:",
                        uniqueRoutes[0],
                      );
                      return null;
                    })()}
                    <select
                      className="form-control"
                      required
                      value={form.route_code}
                      onChange={handleRouteCodeChange}
                    >
                      <option value="">-- Select Route --</option>
                      {uniqueRoutes.map((r) => (
                        <option key={r.route_code} value={r.route_code}>
                          {r.route_name}
                        </option>
                      ))}
                    </select>
                  </>
                )}
              </div>

              <div className="form-group">
                <label className="form-label">Bus Code</label>
                {form.trip_id ? (
                  // Editing: Show as read-only text
                  <div
                    className="form-control"
                    style={{
                      backgroundColor: "#f5f5f5",
                      cursor: "not-allowed",
                    }}
                  >
                    {form.bus_number || "—"}
                  </div>
                ) : (
                  // Creating: Show as dropdown
                  <>
                    <select
                      className="form-control"
                      required
                      value={form.bus_number}
                      onChange={handleBusCodeChange}
                      disabled={!form.route_code || busCodes.length === 0}
                    >
                      <option value="">
                        {!form.route_code
                          ? "-- Select Route First --"
                          : busCodes.length === 0
                            ? "All buses on this route are already scheduled"
                            : "-- Select Bus Code --"}
                      </option>
                      {busCodes.map((b) => (
                        <option key={b.vehicle_code} value={b.vehicle_code}>
                          {b.vehicle_code}
                        </option>
                      ))}
                    </select>
                    {form.route_code && busCodes.length === 0 && (
                      <p
                        style={{
                          fontSize: "12px",
                          color: "var(--warning)",
                          marginTop: "6px",
                        }}
                      >
                        ⚠️ All buses assigned to this route already have an
                        active or scheduled trip. Stop or complete the existing
                        trip first.
                      </p>
                    )}
                  </>
                )}
              </div>

              <div className="form-group">
                <label className="form-label">Driver</label>
                <select
                  className="form-control"
                  required
                  value={form.driver_id}
                  onChange={(e) => {
                    setForm({ ...form, driver_id: e.target.value });
                    setModalError(null);
                  }}
                >
                  <option value="">-- Assign Driver --</option>
                  {drivers.map((d) => (
                    <option
                      key={d.user_id}
                      value={d.user_id}
                      disabled={d.is_busy}
                      style={d.is_busy ? { color: "var(--text-muted)" } : {}}
                    >
                      {d.name}{" "}
                      {d.is_busy
                        ? `(${d.current_trip_status} - ${d.current_bus_number || "No Bus"})`
                        : ""}
                    </option>
                  ))}
                </select>
                {modalError && (
                  <p
                    style={{
                      color: "var(--danger)",
                      fontSize: "13px",
                      marginTop: "8px",
                    }}
                  >
                    {modalError}
                  </p>
                )}
              </div>

              <button
                type="submit"
                className="btn-primary btn-block"
                disabled={submitting}
              >
                {submitting
                  ? form.trip_id
                    ? "Updating..."
                    : "Scheduling..."
                  : form.trip_id
                    ? "Update Trip"
                    : "Schedule Trip"}
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Trips;
